import { isEmpty, uniqBy } from 'lodash';
import { BarGraphIcon, LineGraphIcon, SORTING_ORDER_TYPES, TaboolaGraph } from 'taboola-ultimate-ui';
import { ENTITY } from 'modules/campaigns/config/routes/entity';
import { GRAPHS } from 'modules/campaigns/constants/graphTypes';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import CampaignGraphTooltip from '../../../components/Tooltips/CampainGraphTooltip';
import { DEFAULT_GRAPH_PALETTE } from '../../../services/dynamicColumnsService';
import * as FIELDS from '../../fields';
import {
    dayWeekMonthGetXAxisFormatter,
    dayWeekMonthXAxisMinTickGap,
    tooltipDateFormatter,
} from '../../fields/transformers';
import metrics from '../../metrics';

const { AXIS_SCALE, AXIS_TYPE, AXIS_DOMAIN } = TaboolaGraph;

const barGraph = {
    type: GRAPHS.BAR,
    endpoint: 'campaign-report-by-campaign-daily',
    metrics: [
        metrics.SPENT,
        metrics.VISIBLE_IMPRESSIONS,
        metrics.IMPRESSIONS,
        metrics.CLICKS,
        metrics.VCTR,
        metrics.CTR,
        metrics.AVERAGE_CPC,
        metrics.CVR,
        metrics.CVR_CLICKS,
        metrics.CVR_VIEWS,
        metrics.CONVERSIONS,
        metrics.CONVERSIONS_CLICKS,
        metrics.CONVERSIONS_VIEWS,
        metrics.CPA,
        metrics.CPA_CLICKS,
        metrics.CPA_VIEWS,
        metrics.VCPM,
        metrics.CPM,
        metrics.CONVERSIONS_VALUE,
        metrics.ROAS,
        metrics.ROAS_CLICKS,
        metrics.ROAS_VIEWS,
        metrics.CPCV,
        metrics.VIEWABILITY_RATE,
        metrics.COMPLETION_RATE,
        metrics.HUNDRED_PCT_VIEWABILITY_RATE,
        metrics.COMPLETED_VIEWS,
        metrics.UNIQUE_IMPRESSIONS,
        metrics.UNIQUE_VIEWABLE_IMPRESSIONS,
        metrics.UNIQUE_CLICKS,
    ],
    maxAllowedSelectedMetrics: 4,
    maxXAxisValues: 8,
    shouldUseGridDataSrc: true,
    displayTotalsInLegendRow: false,
    enableLongTextTickFormatting: true,
    graphProps: {
        xAxisDataKey: FIELDS.NAME.field,
    },
    graphTooltip: CampaignGraphTooltip,
    graphToggleIcon: BarGraphIcon,
};

const singleMetricGraph = {
    type: GRAPHS.SINGLE_METRIC_LINE,
    endpoint: 'campaign-report-by-campaign-day',
    metrics: [
        metrics.SPENT,
        metrics.VISIBLE_IMPRESSIONS,
        metrics.IMPRESSIONS,
        metrics.CLICKS,
        metrics.VCTR,
        metrics.AVERAGE_CPC,
        metrics.CVR,
        metrics.CVR_CLICKS,
        metrics.CVR_VIEWS,
        metrics.CONVERSIONS,
        metrics.CONVERSIONS_CLICKS,
        metrics.CONVERSIONS_VIEWS,
        metrics.CPA,
        metrics.CPA_CLICKS,
        metrics.CPA_VIEWS,
        metrics.VCPM,
        metrics.CONVERSIONS_VALUE,
        metrics.ROAS,
        metrics.ROAS_CLICKS,
        metrics.ROAS_VIEWS,
    ],
    maxAllowedSelectedMetrics: 10,
    collatingKey: 'campaign',
    skipSyncWithGrid: true,
    emptyMetricsMsgIdOverride: 'metrics.empty.compareCampaigns',
    numLegendValues: 10,
    getGridDataDependentFilters: reportData => {
        if (!isEmpty(reportData)) {
            return {
                reportEntityIds: reportData.slice(0, 10).map(({ id }) => id),
                reportEntityType: ENTITY.CAMPAIGN,
            };
        }
    },
    defaultSort: [{ field: 'date', order: SORTING_ORDER_TYPES.ASCENDANT }],
    graphProps: {
        xAxisDataKey: FIELDS.DATE.field,
        xAxisMinTickGap: dayWeekMonthXAxisMinTickGap,
        getXAxisFormatter: dayWeekMonthGetXAxisFormatter,
        toolTipLabelFormatter: tooltipDateFormatter,
        xAxisScale: AXIS_SCALE.LINEAR,
        xAxisType: AXIS_TYPE.NUMBER,
        xAxisDomain: [AXIS_DOMAIN.DATA_MIN, AXIS_DOMAIN.DATA_MAX],
        isSingleYAxisDomain: true,
    },
    getMetrics: ({ graphData: { results } }) => {
        const sanitizedResults = uniqBy(results, 'campaign');
        const colors = DEFAULT_GRAPH_PALETTE;
        return sanitizedResults.map((dataPoint, index) => ({
            name: String(dataPoint.campaign),
            label: dataPoint.campaignName,
            color: colors[index],
            active: index < 2,
        }));
    },
    graphTooltip: CampaignGraphTooltip,
    graphToggleIcon: LineGraphIcon,
    accountConfigurations: {
        [FEATURE_FLAGS.SHOW_COMPARE_CAMPAIGNS_GRAPH]: 'true',
    },
};

export const graph = [barGraph, singleMetricGraph];
